import { Sprite, type Texture } from 'pixi.js';
import { pieceUrl, type Density } from '../../assets/sprites.js';
import LAYOUT from './layout.json';

/**
 * Lucky Scratch drawn from its real-3D render (scripts/art/render/scratch.html) in the look of
 * its lobby card, seen straight on: the gold ticket with its cream paper, the silver foil, the
 * foil's curled corner and the card's lucky items. Each item was rendered on the middle cell;
 * the scene places it on each of its cells, scaled from the render's cell to its own, and lays
 * the ticket and the foil over its grid box.
 */

type Box = [number, number, number, number];
const BOXES = LAYOUT.boxes as unknown as Record<string, Box>;
const [GX, GY, GW, GH] = LAYOUT.scene.grid as unknown as Box;
const [CX, CY] = LAYOUT.scene.centre as unknown as [number, number];
const TILE_PX: number = LAYOUT.scene.tilePx;
const PEEL_DEPTH: number = LAYOUT.scene.peelDepth;
/** The grid's side in cells: three cells and the two gaps between them. */
const GRID_TILES = GW / TILE_PX;

export const ITEMS = ['star', 'clover', 'coin', 'ruby', 'seven', 'horseshoe'] as const;
export type ScratchItem = (typeof ITEMS)[number];

/**
 * The card's item each server symbol is drawn as. The server keeps its own names (and the
 * paytable keys them); only the picture changes, the same one under the foil and in the
 * prize table. The filler symbol that never pays (lemon) is the horseshoe.
 */
export const SYMBOL_ITEM: Readonly<Record<string, ScratchItem>> = {
  cherry: 'star',
  bell: 'clover',
  coin: 'coin',
  gem: 'ruby',
  seven: 'seven',
  lemon: 'horseshoe',
};

export const SCRATCH_PIECES = ['frame', 'foil', 'peel', ...ITEMS.map((i) => `item-${i}` as const), 'sparkle', 'glow'] as const;
export type ScratchPiece = (typeof SCRATCH_PIECES)[number];

/** The rendered item for a server symbol name, or null for one with no picture. */
export function itemPiece(symbol: string): `item-${ScratchItem}` | null {
  const item = Object.hasOwn(SYMBOL_ITEM, symbol) ? SYMBOL_ITEM[symbol] : undefined;
  return item ? `item-${item}` : null;
}

/** The prize table's picture of a symbol: the very file the scene draws under the foil. */
export function itemUrl(symbol: string, density: Density): string | null {
  const piece = itemPiece(symbol);
  return piece ? pieceUrl('scratch', piece, density) : null;
}

/** The ticket stands this many cells out from the grid on its widest side; the grid leaves it room. */
export const FRAME_MARGIN_TILES = (() => {
  const [fx, fy, fw, fh] = BOXES.frame!;
  return Math.max(GX - fx, fx + fw - (GX + GW), GY - fy, fy + fh - (GY + GH)) / TILE_PX;
})();

/** Where a still layer (the ticket, the foil, the curled corner) goes, for the scene's grid at (x, y), `w` wide. */
export function gridLayerRect(piece: 'frame' | 'foil' | 'peel', x: number, y: number, w: number): { x: number; y: number; w: number; h: number } {
  const [bx, by, bw, bh] = BOXES[piece]!;
  const k = w / GW;
  return { x: x + (bx - GX) * k, y: y + (by - GY) * k, w: bw * k, h: bh * k };
}

/**
 * The foil the curled corner has lifted off the paper: the triangle at the grid's bottom-right
 * corner, for the scene's grid at (x, y), `w` wide. The scene clears it from the foil it paints.
 */
export function peelTriangle(x: number, y: number, w: number): [number, number][] {
  const leg = PEEL_DEPTH * Math.SQRT2 * (w / GRID_TILES);
  const r = x + w;
  const b = y + w;
  return [
    [r - leg, b],
    [r, b - leg],
    [r, b],
  ];
}

/** An item, centred on its tile, for tiles `tileSize` wide. */
export function placeInTile(tex: Texture, piece: ScratchPiece, tileSize: number): Sprite {
  const [x, y, w, h] = BOXES[piece]!;
  const k = tileSize / TILE_PX;
  const s = new Sprite(tex);
  s.anchor.set((CX - x) / w, (CY - y) / h);
  s.width = w * k;
  s.height = h * k;
  return s;
}
